import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`).then(r => r.json()).then(setConfig).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4"
      style={{
        backgroundImage: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(2,6,23,0.98) 100%), url("https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-md">
        <div className="glass rounded-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className={`overflow-hidden mx-auto ${
              config?.logo_shape === 'circle' ? 'w-28 h-28 rounded-full' :
              config?.logo_shape === 'rectangle' ? 'w-44 h-24 rounded-2xl' :
              'w-28 h-28 rounded-2xl'
            }`}>
              <img src="/logo.jpg" alt="Kapsons" className="w-full h-full object-cover" />
            </div>
          </div>
          {config?.show_app_name !== false && config?.app_name && (
            <h1 className="text-2xl font-bold text-white text-center mb-1">{config.app_name}</h1>
          )}
          {config?.show_tagline !== false && config?.dashboard_headline && (
            <p className="text-dark-400 text-center text-sm mb-2">{config.dashboard_headline}</p>
          )}
          <h2 className="text-lg font-semibold text-white text-center mb-1">Welcome Back</h2>
          <p className="text-dark-400 text-center text-sm mb-6">Sign in to manage your leads and calls</p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-dark-300 mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                placeholder="Enter your username"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-dark-300 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors pr-12"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-dark-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-8 text-center">
            <div>
              <div className="text-primary-400 font-bold text-lg">{config?.total_properties_display || '100+'}</div>
              <div className="text-dark-500 text-xs">Properties</div>
            </div>
            <div>
              <div className="text-green-400 font-bold text-lg">50+</div>
              <div className="text-dark-500 text-xs">Agents</div>
            </div>
            <div>
              <div className="text-blue-400 font-bold text-lg">{config?.total_sales_display || '1K+'}</div>
              <div className="text-dark-500 text-xs">Leads</div>
            </div>
          </div>
          <p className="text-center text-dark-600 text-xs mt-4">Dubai Real Estate &bull; India Sales Team</p>
        </div>
      </div>
    </div>
  );
}
